import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Drawer,
  IconButton,
} from '@mui/material';
import { Add, Close, Delete, Edit } from '@mui/icons-material';
import { formatInTimeZone } from 'date-fns-tz';
import { apiService } from '../../services/apiService';
import { useAuth } from '../../contexts/AuthContext';
import { bookingStatusConfig } from '../../utils/bookingStatus';
import { classColorTokens } from '../../utils/classColors';
import BookingStatusDialog, { isPackBooking } from '../BookingStatusDialog';
import ManualBookingDialog from '../ManualBookingDialog';
import type { ScheduleSession } from './ScheduleCalendarView';
import { SCHED, avatarTint, btnDangerGhostSx, btnPrimarySx, btnSx, iconBtnSx } from './scheduleTheme';
import BookingStatusPill from './BookingStatusPill';
import MemberDetailDialog from './MemberDetailDialog';
import { PaymentPendingTag, isPaymentPending } from './bookingRowBits';

const IST_TIMEZONE = 'Asia/Kolkata';

export interface DrawerBooking {
  _id: string;
  bookingId?: string;
  bookingStatus: string;
  bookingType?: string;
  isPaid?: boolean;
  quantity?: number;
  studio?: any;
  user?: { _id: string; name?: string; image?: string; phoneNumber?: string };
  transaction?: {
    basePrice?: number;
    taxes?: number;
    modeOfPayment?: string;
    purchasedPack?: { title?: string; classesLeft?: number | null; isPaid?: boolean; pack?: { title?: string } } | null;
  };
}

interface SessionBookingsDrawerProps {
  session: ScheduleSession | null;
  color: string;
  canWriteSessions: boolean;
  onClose: () => void;
  /** Bookings changed — the calendar refetches its counts. */
  onChanged: () => void;
  onEditSession: (session: ScheduleSession) => void;
  onDeleteSession: (session: ScheduleSession) => void;
}

const GROUPS: { status: string; label: string; pastLabel?: string }[] = [
  { status: 'CHECKED_IN', label: 'Checked in' },
  { status: 'BOOKED', label: 'Coming', pastLabel: 'Booked, not checked in' },
  { status: 'NO_SHOW', label: 'No show' },
  { status: 'CANCELLED', label: 'Cancelled' },
];

export const initials = (name?: string) =>
  (name || '?')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');

export const bookingSubtitle = (b: DrawerBooking): string => {
  const spots = (b.quantity ?? 1) > 1 ? ` · ${b.quantity} spots` : '';
  if (isPackBooking(b)) {
    const pp = b.transaction?.purchasedPack;
    const title = pp?.pack?.title || pp?.title || 'Pack';
    const left = pp?.classesLeft != null ? ` · ${pp.classesLeft} left` : '';
    return `${title}${left}${spots}`;
  }
  if (b.bookingType === 'trial') return `Trial${spots}`;
  const amount = (b.transaction?.basePrice ?? 0) + (b.transaction?.taxes ?? 0);
  if (b.isPaid === false && amount > 0) {
    return `Drop-in · pays ₹${amount.toLocaleString('en-IN')} at studio${spots}`;
  }
  return `Drop-in · paid${spots}`;
};

const SessionBookingsDrawer: React.FC<SessionBookingsDrawerProps> = ({
  session,
  color,
  canWriteSessions,
  onClose,
  onChanged,
  onEditSession,
  onDeleteSession,
}) => {
  const navigate = useNavigate();
  const { admin } = useAuth();
  const isInstructor = admin?.role === 'instructor';
  const instructorViewAllBookings: boolean =
    isInstructor && (admin?.studio as any)?.whitelabelConfig?.instructorViewAllBookings === true;

  const [bookings, setBookings] = useState<DrawerBooking[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waitingCount, setWaitingCount] = useState<number | null>(null);
  const [statusTarget, setStatusTarget] = useState<{ booking: DrawerBooking; initialStatus?: string } | null>(null);
  const [manualBookingOpen, setManualBookingOpen] = useState(false);
  const [memberTarget, setMemberTarget] = useState<{ user: NonNullable<DrawerBooking['user']>; bookingId: string } | null>(null);

  const tokens = classColorTokens(color);

  // Keyed on id/date, not the object: the calendar refetches after every change
  // and hands back a fresh object for the same session.
  const sessionId = session?._id;
  const sessionDate = session?.date;
  const sessionInstructorId = session?.instructor?._id;

  const fetchBookings = useCallback(async () => {
    if (!sessionId) return;
    // The list endpoint ignores instructorId, so a limited instructor's own
    // classes are enforced here: someone else's class lists nobody.
    if (isInstructor && !instructorViewAllBookings && sessionInstructorId !== admin?.instructorId) {
      setBookings([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Same scoping as the Bookings page's session drill-down.
      const params: any = { sessionId, page: 1, pageSize: 500 };
      if (isInstructor && !instructorViewAllBookings && admin?.instructorId) {
        params.instructorId = admin.instructorId;
      }
      const res = await apiService.getBookings(params);
      setBookings(res?.bookings || []);
    } catch (err: any) {
      setError(err.message || 'Failed to load bookings');
      setBookings([]);
    } finally {
      setLoading(false);
    }
  }, [sessionId, sessionInstructorId, isInstructor, instructorViewAllBookings, admin?.instructorId]);

  useEffect(() => {
    setBookings([]);
    setWaitingCount(null);
    if (!sessionId || !sessionDate) return;
    void fetchBookings();
    // Waitlist count is a nice-to-have — any failure just hides the chip.
    const dateKey = formatInTimeZone(new Date(sessionDate), IST_TIMEZONE, 'yyyy-MM-dd');
    let cancelled = false;
    apiService
      .getSessionWaitlists(dateKey)
      .then((res: any) => {
        if (cancelled) return;
        const row = (res?.sessions || []).find((s: any) => s._id === sessionId);
        setWaitingCount(row ? row.waitingCount ?? 0 : 0);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [sessionId, sessionDate, fetchBookings]);

  const grouped = useMemo(() => {
    const map = new Map<string, DrawerBooking[]>();
    bookings.forEach((b) => {
      const key = GROUPS.some((g) => g.status === b.bookingStatus) ? b.bookingStatus : 'BOOKED';
      map.set(key, [...(map.get(key) || []), b]);
    });
    return map;
  }, [bookings]);

  if (!session) return <Drawer anchor="right" open={false} onClose={onClose} />;

  const start = new Date(session.date);
  const end = new Date(start.getTime() + (session.duration || 0) * 60000);
  const isToday =
    formatInTimeZone(start, IST_TIMEZONE, 'yyyy-MM-dd') === formatInTimeZone(new Date(), IST_TIMEZONE, 'yyyy-MM-dd');
  const isPast = end < new Date();
  const isCancelled = session.isLive === false;
  const dayLabel = isToday ? 'Today' : formatInTimeZone(start, IST_TIMEZONE, 'EEE d MMM');
  const timeLabel = `${formatInTimeZone(start, IST_TIMEZONE, 'h:mm a')} to ${formatInTimeZone(end, IST_TIMEZONE, 'h:mm a')}`;
  const booked = Math.max(0, session.slots - (session.availableSlots ?? session.slots));
  const checkedIn = (grouped.get('CHECKED_IN') || []).reduce((n, b) => n + (b.quantity ?? 1), 0);
  const checkedInColor = bookingStatusConfig('CHECKED_IN').color;
  const sub = [session.instructor?.name ? `With ${session.instructor.name}` : null, session.classType?.title || session.classType?.name]
    .filter(Boolean)
    .join(' · ');

  const openUser = (b: DrawerBooking) => {
    if (b.user?._id) setMemberTarget({ user: b.user, bookingId: b._id });
  };

  const statChip = (label: string, bg: string, color: string = SCHED.ink, onClick?: () => void) => (
    <Box
      component={onClick ? 'button' : 'span'}
      {...(onClick ? { type: 'button', onClick } : {})}
      sx={{
        fontFamily: SCHED.font,
        fontSize: '14px',
        fontWeight: 600,
        p: '8px 12px',
        borderRadius: '12px',
        border: 0,
        backgroundColor: bg,
        color,
        cursor: onClick ? 'pointer' : 'default',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </Box>
  );

  // Every status change goes through the shared BookingStatusDialog; the pill
  // shows the booking's current status in the panel's usual status colours.
  const renderAction = (b: DrawerBooking) => (
    <BookingStatusPill status={b.bookingStatus} onClick={() => setStatusTarget({ booking: b })} />
  );

  return (
    <>
      <Drawer
        anchor="right"
        open={Boolean(session)}
        onClose={onClose}
        slotProps={{ backdrop: { sx: { backgroundColor: 'rgba(28,24,26,0.40)' } } }}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 560 },
            borderRadius: 0,
            display: 'flex',
            flexDirection: 'column',
            fontFamily: SCHED.font,
            color: SCHED.ink,
            boxShadow: '-20px 0 50px rgba(40,20,26,0.18)',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px', p: { xs: '22px 20px 16px', sm: '28px 32px 18px' }, borderBottom: `1px solid ${SCHED.track}`, flexShrink: 0 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', flexGrow: 1, minWidth: 0 }}>
            <Box component="span" sx={{ alignSelf: 'flex-start', fontSize: '12px', fontWeight: 700, p: '3px 10px', borderRadius: '999px', backgroundColor: tokens.bg, color: tokens.text, whiteSpace: 'nowrap' }}>
              {session.className}
            </Box>
            <Box component="h2" sx={{ m: 0, fontSize: { xs: '22px', sm: '26px' }, fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.25 }}>
              {dayLabel}, {timeLabel}
            </Box>
            {sub && (
              <Box component="span" sx={{ fontSize: '14px', color: SCHED.muted }}>
                {sub}
              </Box>
            )}
          </Box>
          <IconButton onClick={onClose} aria-label="Close" sx={{ ...iconBtnSx(44), border: 0, backgroundColor: SCHED.hover, '&:hover': { backgroundColor: SCHED.track } }}>
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>

        {/* Body */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '18px', p: { xs: '16px 20px', sm: '18px 32px' }, flexGrow: 1, overflowY: 'auto' }}>
          {isCancelled && (
            <Box sx={{ p: '14px 16px', borderRadius: '14px', backgroundColor: SCHED.track, fontSize: '14px', fontWeight: 600 }}>
              This session is cancelled.
            </Box>
          )}

          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {statChip(`${booked}/${session.slots} booked`, SCHED.hover)}
            {!isCancelled && statChip(`${checkedIn} checked in`, `${checkedInColor}14`, checkedInColor)}
            {!isCancelled && !isPast && waitingCount != null &&
              statChip(`${waitingCount} waiting`, SCHED.waitBg, SCHED.waitText, () => navigate('/session-waitlist'))}
            <Box sx={{ flexGrow: 1 }} />
            {!isInstructor && !isCancelled && !isPast && (
              <Button startIcon={<Add sx={{ fontSize: 18 }} />} onClick={() => setManualBookingOpen(true)} sx={btnPrimarySx}>
                Create Booking
              </Button>
            )}
          </Box>

          {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} sx={{ color: SCHED.muted }} />
            </Box>
          ) : bookings.length === 0 ? (
            <Box sx={{ py: '24px', textAlign: 'center', fontSize: '14px', color: SCHED.muted }}>No bookings for this session yet.</Box>
          ) : (
            GROUPS.filter((g) => (grouped.get(g.status) || []).length > 0).map((g) => {
              const rows = grouped.get(g.status)!;
              return (
                <Box key={g.status} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box component="h3" sx={{ m: 0, mb: '4px', fontSize: '16px', fontWeight: 700 }}>
                    {isPast && g.pastLabel ? g.pastLabel : g.label} ({rows.length})
                  </Box>
                  {rows.map((b) => (
                    <Box
                      key={b._id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                        minHeight: 64,
                        borderBottom: `1px solid ${SCHED.rowLine}`,
                        opacity: b.bookingStatus === 'CANCELLED' ? 0.6 : 1,
                      }}
                    >
                      <Avatar
                        src={b.user?.image}
                        sx={{ width: 40, height: 40, fontSize: '14px', fontWeight: 700, fontFamily: SCHED.font, backgroundColor: avatarTint(b.user?._id || b._id), color: SCHED.ink }}
                      >
                        {initials(b.user?.name)}
                      </Avatar>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', flexGrow: 1, minWidth: 0 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                        <Box
                          component="button"
                          type="button"
                          onClick={() => openUser(b)}
                          disabled={!b.user?._id}
                          sx={{
                            alignSelf: 'flex-start',
                            border: 0,
                            backgroundColor: 'transparent',
                            p: 0,
                            fontFamily: SCHED.font,
                            fontSize: '15px',
                            fontWeight: 600,
                            color: SCHED.ink,
                            textAlign: 'left',
                            cursor: b.user?._id ? 'pointer' : 'default',
                            maxWidth: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            '&:hover:not(:disabled)': { textDecoration: 'underline' },
                          }}
                        >
                          {b.user?.name || 'Unknown member'}
                        </Box>
                        {isPaymentPending(b) && <PaymentPendingTag />}
                        </Box>
                        <Box component="span" sx={{ fontSize: '13px', color: SCHED.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {bookingSubtitle(b)}
                        </Box>

                      </Box>
                      {renderAction(b)}
                    </Box>
                  ))}
                </Box>
              );
            })
          )}
        </Box>

        {/* Footer — the existing flows for the whole session */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', p: { xs: '14px 20px', sm: '16px 32px' }, borderTop: `1px solid ${SCHED.track}`, flexShrink: 0 }}>
          {canWriteSessions && (
            <>
              <Button startIcon={<Edit sx={{ fontSize: 18 }} />} onClick={() => onEditSession(session)} sx={btnSx}>
                Edit
              </Button>
              <Box sx={{ flexGrow: 1 }} />
              <Button startIcon={<Delete sx={{ fontSize: 18 }} />} onClick={() => onDeleteSession(session)} sx={btnDangerGhostSx}>
                Delete
              </Button>
            </>
          )}
        </Box>
      </Drawer>

      <BookingStatusDialog
        open={Boolean(statusTarget)}
        booking={statusTarget?.booking ?? null}
        initialStatus={statusTarget?.initialStatus}
        onClose={() => setStatusTarget(null)}
        onUpdated={(bookingId, newStatus) => {
          setBookings((prev) => prev.map((b) => (b._id === bookingId ? { ...b, bookingStatus: newStatus } : b)));
          setStatusTarget(null);
          onChanged();
        }}
        onError={setError}
      />

      <MemberDetailDialog
        member={memberTarget?.user ?? null}
        bookingId={memberTarget?.bookingId}
        instructorName={session?.instructor?.name}
        onClose={() => setMemberTarget(null)}
      />

      {!isInstructor && (
        <ManualBookingDialog
          open={manualBookingOpen}
          onClose={() => setManualBookingOpen(false)}
          onSuccess={() => {
            setManualBookingOpen(false);
            void fetchBookings();
            onChanged();
          }}
          prefilledSession={session as any}
        />
      )}
    </>
  );
};

export default SessionBookingsDrawer;
